import React, { createContext, useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import NETWORK from '../config/network';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../config/contract';
import { USDT_ADDRESS, USDT_ABI } from '../config/usdt';

export const Web3Context = createContext();

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [usdtContract, setUsdtContract] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [notification, setNotification] = useState(null);

  const providerRef = useRef(null);
  const isInitializedRef = useRef(false);

  const showNotification = (message, type = 'info', duration = 5000) => {
    setNotification({ message, type, id: Date.now() });
    if (duration > 0) {
      setTimeout(() => setNotification(null), duration);
    }
  };

  const switchNetwork = async () => {
    if (!window.ethereum) return false;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: NETWORK.chainId }],
      });
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: NETWORK.chainId,
              chainName: NETWORK.chainName,
              rpcUrls: NETWORK.rpcUrls,
              blockExplorerUrls: NETWORK.blockExplorerUrls,
              nativeCurrency: NETWORK.nativeCurrency,
            }],
          });
          return true;
        } catch (addError) {
          showNotification('Failed to add network. Please add it manually.', 'error');
          return false;
        }
      }
      showNotification('Failed to switch network.', 'error');
      return false;
    }
  };

  const checkNetwork = async (ethProvider) => {
    try {
      const network = await ethProvider.getNetwork();
      return network.chainId === NETWORK.chainIdDecimal;
    } catch {
      return false;
    }
  };

  const setupContracts = (ethSigner) => {
    const mainContract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, ethSigner);
    const usdtCon = new ethers.Contract(USDT_ADDRESS, USDT_ABI, ethSigner);
    setContract(mainContract);
    setUsdtContract(usdtCon);
    return mainContract;
  };

  const loadUserData = async (mainContract, userAddress) => {
    try {
      const uid = await mainContract.getUserIdByAddress(userAddress);
      const uidNum = uid.toNumber();
      if (uidNum > 0) {
        setUserId(uidNum);
        setIsRegistered(true);
      } else {
        setUserId(null);
        setIsRegistered(false);
      }
      // Check admin/owner
      try {
        const [ownerAddr, adminAddr] = await Promise.all([mainContract.owner(), mainContract.admin()]);
        const isOwnerUser = userAddress.toLowerCase() === ownerAddr.toLowerCase();
        const isAdminUser = isOwnerUser || userAddress.toLowerCase() === adminAddr.toLowerCase();
        setIsAdmin(isAdminUser);
        setIsOwner(isOwnerUser);
      } catch {
        setIsAdmin(false);
        setIsOwner(false);
      }
    } catch {
      setUserId(null);
      setIsRegistered(false);
      setIsAdmin(false);
      setIsOwner(false);
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      showNotification('No wallet detected. Please install Trust Wallet or MetaMask.', 'error');
      return;
    }

    if (isConnecting) return;
    setIsConnecting(true);

    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      if (!accounts || accounts.length === 0) {
        showNotification('No accounts found.', 'error');
        setIsConnecting(false);
        return;
      }

      const ethProvider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      providerRef.current = ethProvider;

      const correct = await checkNetwork(ethProvider);
      if (!correct) {
        showNotification('Switching to correct network...', 'info');
        const switched = await switchNetwork();
        if (!switched) {
          setIsConnecting(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }

      const freshProvider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      providerRef.current = freshProvider;
      const ethSigner = freshProvider.getSigner();
      const userAddress = await ethSigner.getAddress();

      setProvider(freshProvider);
      setSigner(ethSigner);
      setAccount(userAddress);
      setIsCorrectNetwork(true);

      const mainContract = setupContracts(ethSigner);
      await loadUserData(mainContract, userAddress);

      showNotification('Wallet connected successfully!', 'success');
    } catch (err) {
      console.error('Connect error:', err);
      showNotification(err.message || 'Failed to connect wallet.', 'error');
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setContract(null);
    setUsdtContract(null);
    setUserId(null);
    setIsRegistered(false);
    setIsCorrectNetwork(false);
    setIsAdmin(false);
    setIsOwner(false);
    providerRef.current = null;
    showNotification('Wallet disconnected.', 'info');
  };

  const refreshUserData = async () => {
    if (contract && account) {
      await loadUserData(contract, account);
    }
  };

  useEffect(() => {
    if (!window.ethereum || isInitializedRef.current) return;
    isInitializedRef.current = true;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnectWallet();
      } else if (account && accounts[0].toLowerCase() !== account.toLowerCase()) {
        setAccount(accounts[0]);
        const ethProvider = new ethers.providers.Web3Provider(window.ethereum, 'any');
        providerRef.current = ethProvider;
        const ethSigner = ethProvider.getSigner();
        setProvider(ethProvider);
        setSigner(ethSigner);
        const mainContract = setupContracts(ethSigner);
        loadUserData(mainContract, accounts[0]);
        showNotification('Account changed.', 'info');
      }
    };

    const handleChainChanged = async () => {
      if (!account) return;
      const ethProvider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      providerRef.current = ethProvider;
      const correct = await checkNetwork(ethProvider);

      if (!correct) {
        setIsCorrectNetwork(false);
        showNotification('Wrong network detected. Switching...', 'warning');
        const switched = await switchNetwork();
        if (switched) {
          await new Promise((r) => setTimeout(r, 1500));
          const freshProvider = new ethers.providers.Web3Provider(window.ethereum, 'any');
          providerRef.current = freshProvider;
          const ethSigner = freshProvider.getSigner();
          setProvider(freshProvider);
          setSigner(ethSigner);
          setIsCorrectNetwork(true);
          const mainContract = setupContracts(ethSigner);
          await loadUserData(mainContract, account);
        }
      } else {
        setIsCorrectNetwork(true);
        const ethSigner = ethProvider.getSigner();
        setProvider(ethProvider);
        setSigner(ethSigner);
        const mainContract = setupContracts(ethSigner);
        await loadUserData(mainContract, account);
      }
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      if (window.ethereum.removeListener) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }, [account]);

  return (
    <Web3Context.Provider value={{
      account,
      provider,
      signer,
      contract,
      usdtContract,
      userId,
      isRegistered,
      isConnecting,
      isCorrectNetwork,
      isAdmin,
      isOwner,
      notification,
      connectWallet,
      disconnectWallet,
      refreshUserData,
      showNotification,
      switchNetwork,
    }}>
      {children}
    </Web3Context.Provider>
  );
};
